import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { humanizeError } from '../../utils/errorHandling';
import {
  PROFILE_LINKS_PRIVACY_NOTE,
  PROFILE_LINKS_SUBTITLE,
  PROFILE_LINKS_TITLE,
  PROFILE_PLATFORMS,
  blankProfileLink,
  normalizeProfileLinkDraft,
  profileLinkDisplayLabel,
  profilePlatformLabel,
} from '../../utils/profileLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProfileLinkRow({ link, canEdit, onEdit, onDelete }) {
  const label = profileLinkDisplayLabel(link);
  return (
    <li
      className="group flex items-start justify-between gap-3 rounded-xl border border-[hsl(38_14%_86%)]/55 bg-white/50 px-4 py-3"
      data-testid={`profile-link-${link.id}`}
    >
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {profilePlatformLabel(link.platform)}
        </p>
        <p className="font-serif text-sm text-foreground/90">{label}</p>
        {link.note ? <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{link.note}</p> : null}
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--studio-accent,38_36%_42%))] underline-offset-4 hover:underline"
        >
          Open link
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      </div>
      {canEdit ? (
        <div className="flex shrink-0 gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(link)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Edit</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive/80 hover:text-destructive"
            onClick={() => onDelete(link)}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Remove</span>
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Optional external profile links — private archive context, not a social feed.
 */
export function ProfileLinksSection({ personId, links = [], canEdit = false, onUpdated }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(() => blankProfileLink());
  const [saving, setSaving] = useState(false);

  const sortedLinks = useMemo(
    () =>
      [...links].sort(
        (a, b) =>
          PROFILE_PLATFORMS.findIndex((p) => p.id === a.platform) -
          PROFILE_PLATFORMS.findIndex((p) => p.id === b.platform)
      ),
    [links]
  );
  const hasContent = sortedLinks.length > 0;

  const persistLinks = async (nextLinks) => {
    if (!personId) return;
    setSaving(true);
    try {
      const response = await axios.put(`${API}/individuals/${personId}`, { profile_links: nextLinks });
      onUpdated?.(response.data?.profile_links || nextLinks);
      toast.success('Profile links saved.');
    } catch (err) {
      toast.error(humanizeError(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setDraft(blankProfileLink());
    setDialogOpen(true);
  };

  const openEdit = (link) => {
    setDraft({
      ...blankProfileLink(link.platform),
      ...link,
      label: link.label || '',
      note: link.note || '',
    });
    setDialogOpen(true);
  };

  const handleSaveDraft = async () => {
    const normalized = normalizeProfileLinkDraft(draft);
    if (!normalized) {
      toast.error('Please add a valid https link.');
      return;
    }
    const exists = links.some((i) => i.id === normalized.id);
    const next = exists ? links.map((i) => (i.id === normalized.id ? normalized : i)) : [...links, normalized];
    try {
      await persistLinks(next);
      setDialogOpen(false);
    } catch {
      /* toast handled */
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Remove this ${profilePlatformLabel(link.platform)} link?`)) return;
    try {
      await persistLinks(links.filter((i) => i.id !== link.id));
    } catch {
      /* toast handled */
    }
  };

  if (!hasContent && !canEdit) return null;

  return (
    <>
      <Card
        className="border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]/90"
        data-testid="profile-links-section"
      >
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-serif text-lg">
                <Link2 className="h-4 w-4 text-[hsl(var(--studio-accent,38_36%_42%))]" aria-hidden />
                {PROFILE_LINKS_TITLE}
              </CardTitle>
              <CardDescription className="mt-1">{PROFILE_LINKS_SUBTITLE}</CardDescription>
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 rounded-full border-[#8B7355]/30 text-[#5c4a32] hover:bg-[#8B7355]/8"
                onClick={openCreate}
                data-testid="add-profile-link-btn"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add link
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasContent ? (
            <p className="font-serif text-sm leading-relaxed text-muted-foreground/85">
              Add their Instagram, TikTok, Facebook, YouTube, LinkedIn, or website if it helps family find more of
              their story — always optional, never auto-published.
            </p>
          ) : (
            <ul className="space-y-2.5">
              {sortedLinks.map((link) => (
                <ProfileLinkRow
                  key={link.id}
                  link={link}
                  canEdit={canEdit}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </ul>
          )}
          <p className="text-[11px] leading-relaxed text-muted-foreground/80">{PROFILE_LINKS_PRIVACY_NOTE}</p>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="profile-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {draft.id && links.some((i) => i.id === draft.id) ? 'Edit profile link' : 'Add profile link'}
            </DialogTitle>
            <DialogDescription>Links open externally. Nothing is published or verified automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={draft.platform} onValueChange={(v) => setDraft((d) => ({ ...d, platform: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROFILE_PLATFORMS.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={draft.url}
                onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
                placeholder="https://…"
                type="url"
              />
            </div>
            <div className="space-y-2">
              <Label>Label (optional)</Label>
              <Input
                value={draft.label}
                onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                placeholder={profilePlatformLabel(draft.platform)}
              />
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                value={draft.note}
                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                rows={2}
                placeholder="Private note for your archive — not shown publicly"
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ProfileLinksSection;
